import axios from "axios";
import { publicRoutes } from "./routes/public.routes";
import { module1Routes } from "./routes/module1.routes";
import { module2Routes } from "./routes/module2.routes";
import { isClientFacingRole } from "@/utils/auth";
import { hasAnyPermission, isItOnlyStaff } from "@/utils/permissions";
import { canAccessMandatModule } from "@/utils/postLoginRedirect";
import { ensureAuthenticated } from "@/utils/sessionAuth";
import { getVerifiedRole, hasVerifiedProfile, isVerifiedAdmin } from "@/utils/verifiedProfile";
import { routeRecordIsProtected } from "./routeMeta";
import NotFound from "@/views/public/NotFoundPage.vue";

export { ROLES } from "./routes/constants";

const routes = [
  ...publicRoutes,
  ...module1Routes,
  ...module2Routes,
  {
    path: "/:pathMatch(.*)*",
    name: "Page non trouvée",
    component: NotFound,
    meta: { title: "Page non trouvee", robots: "noindex, nofollow" },
  },
];

function defaultDashboardPath(path, role) {
  const p = String(path || "");
  if (p.startsWith("/actions-mandat")) {
    if (isClientFacingRole(role)) {
      if (!canAccessMandatModule(role)) return "/analyse-patrimoniale/dashboards";
      return "/actions-mandat/mes-recommandations";
    }
    return "/actions-mandat/dashboards";
  }
  return "/analyse-patrimoniale/dashboards";
}

// Guard de navigation (vérifie cookies httpOnly via GET /api/profile/)
export const navigationGuard = async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => routeRecordIsProtected(record));

  if (!requiresAuth) {
    next();
    return;
  }

  const authenticated = await ensureAuthenticated(axios);
  if (!authenticated) {
    next({
      path: "/connexion",
      query: { redirect: to.fullPath },
    });
    return;
  }

  if (!hasVerifiedProfile()) {
    next({ path: "/connexion", query: { redirect: to.fullPath } });
    return;
  }

  const userRole = getVerifiedRole();

  const IT_DIAGNOSTIC_PATH = "/analyse-patrimoniale/diagnostic-it";
  const IT_ALLOWED_PATHS = [
    IT_DIAGNOSTIC_PATH,
    "/analyse-patrimoniale/profil",
    "/analyse-patrimoniale/modeles-pdf",
  ];
  if (isItOnlyStaff() && !IT_ALLOWED_PATHS.includes(to.path)) {
    next({ path: IT_DIAGNOSTIC_PATH });
    return;
  }

  if (
    String(to.path || "").startsWith("/actions-mandat") &&
    isClientFacingRole(userRole) &&
    !canAccessMandatModule(userRole)
  ) {
    next({ path: "/analyse-patrimoniale/dashboards" });
    return;
  }

  const permRecords = to.matched.filter(
    (record) =>
      record.meta &&
      Array.isArray(record.meta.requiresAnyPermission) &&
      record.meta.requiresAnyPermission.length
  );
  for (const record of permRecords) {
    if (!hasAnyPermission(record.meta.requiresAnyPermission)) {
      next({ path: defaultDashboardPath(to.path, userRole) });
      return;
    }
  }

  const requiresRoleRecord = to.matched.find((record) => record.meta.requiresRole);
  if (requiresRoleRecord) {
    const required = requiresRoleRecord.meta.requiresRole;
    const isAllowed = Array.isArray(required)
      ? required.includes(userRole)
      : required === userRole;
    if (!isAllowed && !isVerifiedAdmin()) {
      next({ path: defaultDashboardPath(to.path, userRole) });
      return;
    }
  }

  next();
};

export default routes;
